import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ModuleIcon } from './ModuleIcon';
import { MODULE_BRAND_ICON } from '../lib/moduleIcons';
import type { ModuleId } from '../lib/modules';
import { MODULES, moduleForPath, visibleModules, visibleModuleItems } from '../lib/modules';

/**
 * The left-hand navigation: a MODULE RAIL, and beside it that module's own
 * pages.
 *
 * It used to be one flat list of Work / Delegation / Manage sections
 * spanning everything. With six modules that list became the only map of a
 * product that had outgrown it - CG Sign and the vault sat as single lines
 * among a dozen task links, which made them look like features of the task
 * app rather than things in their own right.
 *
 * THE RAIL IS THE MAP. Which module you are in is read from the URL, never
 * from what was last clicked, so a deep link, a refresh or the browser's
 * back button all highlight correctly.
 *
 * A MODULE WITH ONE SCREEN GETS NO PANEL. CG Sign, Meetings, CG Vault and
 * Audit Trail are each a single page; a panel holding one link is worse
 * than no panel, so the page takes the full width instead.
 */

/** Initials, the same fallback the API uses when there is no avatar. */
function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/** The rail's module mark. A component so the lookup stays out of the JSX. */
function BrandMark({ id, className }: { id: ModuleId; className?: string }) {
  const Icon = MODULE_BRAND_ICON[id];
  return <Icon className={className} />;
}

export function Sidebar({
  collapsed = false,
  onNavigate,
}: {
  /** Rail only: the module panel hides and the rail keeps its tooltips. */
  collapsed?: boolean;
  onNavigate?: () => void;
} = {}) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();

  const modules = visibleModules(user?.role.systemKey);
  const activeId = moduleForPath(pathname);
  const active = MODULES.find((m) => m.id === activeId);

  // (Lottery's pages inside To-Do need the person's own grants - lottery.view / lottery.upload.)
  const items = visibleModuleItems(active?.items, user?.role.systemKey, user?.permissions ?? []);
  const showPanel = !collapsed && items.length > 0;

  return (
    <nav className="flex h-full shrink-0">
      {/* ---------------------------------------------------- the rail -- */}
      <div className="flex w-16 flex-col items-center border-r border-slate-200 bg-slate-100/80 py-4">
        <img src="/favicon-96x96.png" alt="" className="h-8 w-8 shrink-0" />

        <ul className="mt-5 flex flex-1 flex-col gap-1.5">
          {modules.map((m) => {
            const on = m.id === activeId;
            return (
              <li key={m.id}>
                <NavLink
                  to={m.home}
                  onClick={onNavigate}
                  title={m.label}
                  aria-label={m.label}
                  aria-current={on ? 'page' : undefined}
                  className={[
                    'grid h-11 w-11 place-items-center rounded-xl transition',
                    on
                      ? 'bg-white text-brand-700 shadow-sm ring-1 ring-slate-200'
                      : 'text-slate-500 hover:bg-white/70 hover:text-slate-800',
                  ].join(' ')}
                >
                  {/*
                    The brand mark, not the monochrome glyph. It keeps its
                    own colours whether the module is active or not -
                    recolouring a brand mark on hover is what the plate
                    behind it is for. The glyph set is still used for the
                    items INSIDE a module, which do signal state.
                  */}
                  <BrandMark id={m.id} className="h-6 w-6 shrink-0" />
                </NavLink>
              </li>
            );
          })}
        </ul>

        {/* The person, at the foot of the rail. Settings, theme and the
            assistant live in the footer bar, not here. */}
        <button
          type="button"
          onClick={logout}
          title="Sign out"
          aria-label="Sign out"
          className="mt-2 grid h-10 w-10 place-items-center rounded-xl text-slate-500 transition hover:bg-white hover:text-slate-800"
        >
          <ModuleIcon name="logout" className="h-5 w-5 shrink-0" />
        </button>
      </div>

      {/* --------------------------------------------- the module panel -- */}
      {showPanel && (
        <div className="flex w-56 flex-col border-r border-slate-200 bg-slate-50">
          <div className="px-4 py-5">
            {/* A plain label: switching organisation lives on the Dashboard. */}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">
                {user?.organisation.name ?? 'Charles Technologies'}
              </p>
              <p className="text-xs text-slate-500">{active?.label}</p>
            </div>
          </div>

          <ul className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-4">
            {items.map((item, i) => (
              <li key={item.to}>
                {/* A group's heading above its first item (Lottery inside To-Do). */}
                {item.group && item.group !== items[i - 1]?.group && (
                  <p className="px-2.5 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    {item.group}
                  </p>
                )}
                <NavLink
                  to={item.to}
                  /* /lottery must not light up on /lottery/upload. */
                  end={item.to === '/lottery'}
                  onClick={onNavigate}
                  /* `end` so /tasks does not stay lit on /tasks/123 while a
                     more specific link is the real destination. */
                  className={({ isActive }) =>
                    [
                      'flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition',
                      isActive
                        ? 'bg-white text-brand-700 shadow-sm ring-1 ring-slate-200'
                        : 'text-slate-600 hover:bg-white hover:text-slate-900',
                    ].join(' ')
                  }
                >
                  <ModuleIcon name={item.icon} />
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="border-t border-slate-200 p-3">
            <div className="flex items-center gap-2.5 px-2 py-2">
              {user?.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt=""
                  className="h-8 w-8 shrink-0 rounded-full object-cover"
                />
              ) : (
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
                  {initials(user?.displayName ?? '')}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">
                  {user?.displayName ?? 'Signed in'}
                </p>
                <p className="truncate text-xs text-slate-500">{user?.role.name}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
